import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";

/**
 * Auto-translate arbitrary Czech text nodes in the DOM to the current UI language.
 * - Runs only when i18n language differs from "cs".
 * - Collects text nodes via a TreeWalker + MutationObserver, dedupes across the page,
 *   batches to a Supabase edge function which caches translations in
 *   public.content_translations (table_name = "__auto__").
 * - Stores the original text on each translated node so switching back to CS restores it.
 *
 * Opt-out: any ancestor with `data-no-translate` (or `translate="no"`) is skipped.
 * Also skips <code>, <pre>, <script>, <style>, <textarea>, <input>, elements with
 * dir attribute pointing to code, and text that looks non-Czech / numeric / short.
 */

// Cache original CS text per text-node so we can restore or re-translate to a new lang.
const originalText = new WeakMap<Text, string>();

// In-memory translation cache for the current session, keyed by `${lang}::${czech}`.
const memCache = new Map<string, string>();

// Persistent cache in localStorage, per language, so second visit is instant.
const LS_PREFIX = "ut_tr_v1::";
const KNOWN_MISSING_PREFIX = "ut_tr_missing_v1::";

const knownMissing = new Set<string>();

function loadPersistedCache(lang: string) {
  try {
    const raw = localStorage.getItem(LS_PREFIX + lang);
    if (!raw) return;
    const obj = JSON.parse(raw) as Record<string, string>;
    for (const [k, v] of Object.entries(obj)) {
      if (typeof v === "string" && v.length > 0) memCache.set(`${lang}::${k}`, v);
    }
  } catch { /* ignore */ }

  try {
    const rawMissing = localStorage.getItem(KNOWN_MISSING_PREFIX + lang);
    if (!rawMissing) return;
    const values = JSON.parse(rawMissing) as string[];
    if (Array.isArray(values)) {
      for (const value of values) {
        if (typeof value === "string" && value.length > 0) knownMissing.add(`${lang}::${value}`);
      }
    }
  } catch { /* ignore */ }
}
let persistTimer: number | null = null;
function schedulePersist(lang: string) {
  if (persistTimer !== null) return;
  persistTimer = window.setTimeout(() => {
    persistTimer = null;
    try {
      const prefix = `${lang}::`;
      const obj: Record<string, string> = {};
      for (const [k, v] of memCache) {
        if (k.startsWith(prefix)) obj[k.slice(prefix.length)] = v;
      }
      localStorage.setItem(LS_PREFIX + lang, JSON.stringify(obj));

      const missingPrefix = `${lang}::`;
      const missing = Array.from(knownMissing)
        .filter((key) => key.startsWith(missingPrefix))
        .map((key) => key.slice(missingPrefix.length))
        .slice(0, 600);
      localStorage.setItem(KNOWN_MISSING_PREFIX + lang, JSON.stringify(missing));
    } catch { /* quota etc. */ }
  }, 500);
}

const SKIP_TAGS = new Set([
  "SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE", "TEXTAREA", "INPUT",
  "SVG", "PATH", "CANVAS", "MATH",
]);

function isSkippableAncestor(el: Element | null): boolean {
  let cur: Element | null = el;
  while (cur) {
    if (SKIP_TAGS.has(cur.tagName)) return true;
    if (cur.getAttribute("data-no-translate") !== null) return true;
    if (cur.getAttribute("translate") === "no") return true;
    if (cur.getAttribute("contenteditable") === "true") return true;
    cur = cur.parentElement;
  }
  return false;
}

// Heuristic: is the text worth translating? (Czech-looking or plain Latin words)
function isTranslatable(raw: string): boolean {
  const t = raw.trim();
  if (t.length < 2) return false;
  // Skip pure numbers / punctuation / symbols
  if (!/\p{L}/u.test(t)) return false;
  // Skip if only digits + units-like patterns
  if (/^[\d\s\-\/.,+×xX*×%°]+$/.test(t)) return false;
  // Skip emails and URLs
  if (/@|https?:\/\//.test(t)) return false;
  // Skip single all-caps words that look like codes/IDs (e.g. "SVG", "PDF")
  if (t.length <= 4 && /^[A-Z0-9]+$/.test(t)) return false;
  return true;
}

function collectTextNodes(root: Node, out: Text[]) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const text = node as Text;
      const parent = text.parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;
      if (isSkippableAncestor(parent)) return NodeFilter.FILTER_REJECT;
      const value = originalText.get(text) ?? text.nodeValue ?? "";
      if (!isTranslatable(value)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let n: Node | null;
  while ((n = walker.nextNode())) out.push(n as Text);
}

const AutoTranslator = () => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "cs").split("-")[0];
  const inflight = useRef<Promise<void> | null>(null);
  const pending = useRef<Set<Text>>(new Set());
  const scheduled = useRef<number | null>(null);

  useEffect(() => {
    // Restore originals whenever language changes (before applying new one)
    return () => {
      // No teardown needed here; effect below handles lang transitions.
    };
  }, []);

  useEffect(() => {
    // When switching to CS (or back), restore every stored original.
    if (lang === "cs") {
      // Walk every stored node and restore
      const all: Text[] = [];
      collectTextNodes(document.body, all);
      // Also cover nodes we already translated (they may no longer match filter)
      // Iterate over WeakMap by walking every text node in body:
      const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
      let n: Node | null;
      while ((n = walker.nextNode())) {
        const t = n as Text;
        const orig = originalText.get(t);
        if (orig && t.nodeValue !== orig) t.nodeValue = orig;
      }
      return;
    }

    let cancelled = false;

    // Warm in-memory cache from localStorage
    loadPersistedCache(lang);

    // Apply any cached translation synchronously (instant on repeat visits)
    const applyCachedNow = (nodes: Text[]) => {
      for (const node of nodes) {
        const orig = originalText.get(node) ?? node.nodeValue ?? "";
        if (!originalText.has(node)) originalText.set(node, orig);
        const cached = memCache.get(`${lang}::${orig}`);
        if (cached && cached !== orig && node.isConnected && node.nodeValue !== cached) {
          node.nodeValue = cached;
        }
      }
    };

    const flush = async () => {
      if (cancelled) return;
      const batch = Array.from(pending.current);
      pending.current.clear();
      if (batch.length === 0) return;

      // Group by original text
      const byText = new Map<string, Text[]>();
      for (const node of batch) {
        const orig = originalText.get(node) ?? node.nodeValue ?? "";
        if (!originalText.has(node)) originalText.set(node, orig);
        if (!isTranslatable(orig)) continue;
        const arr = byText.get(orig) ?? [];
        arr.push(node);
        byText.set(orig, arr);
      }

      const texts = Array.from(byText.keys());
      const needFetch: string[] = [];
      const resolved = new Map<string, string>();
      for (const text of texts) {
        const cached = memCache.get(`${lang}::${text}`);
        if (cached) resolved.set(text, cached);
        else if (!knownMissing.has(`${lang}::${text}`)) needFetch.push(text);
      }

      // Apply cached immediately, before awaiting network
      for (const [text, nodes] of byText) {
        const translated = resolved.get(text);
        if (!translated || translated === text) continue;
        for (const node of nodes) {
          if (node.isConnected && node.nodeValue !== translated) node.nodeValue = translated;
        }
      }

      if (needFetch.length === 0) return;

      // Chunk large fetches so first results render sooner
      const CHUNK = 60;
      const chunks: string[][] = [];
      for (let i = 0; i < needFetch.length; i += CHUNK) chunks.push(needFetch.slice(i, i + CHUNK));

      await Promise.all(chunks.map(async (chunk) => {
        try {
          const { data, error } = await supabase.functions.invoke("translate-batch", {
            body: { lang, texts: chunk, cacheOnly: true },
          });
          if (error || !data?.translations) return;
          const entries = Object.entries(data.translations as Record<string, string>);
          let cacheChanged = false;
          for (const [k, v] of entries) {
            if (typeof v === "string" && v.length > 0 && v !== k) {
              memCache.set(`${lang}::${k}`, v);
              resolved.set(k, v);
              knownMissing.delete(`${lang}::${k}`);
              cacheChanged = true;
            } else {
              knownMissing.add(`${lang}::${k}`);
              cacheChanged = true;
            }
          }
          if (cancelled) return;
          // Apply this chunk's results ASAP
          for (const [k, v] of entries) {
            const nodes = byText.get(k);
            if (!nodes || !v || v === k) continue;
            for (const node of nodes) {
              if (node.isConnected && node.nodeValue !== v) node.nodeValue = v;
            }
          }
          if (cacheChanged) schedulePersist(lang);
        } catch { /* silent */ }
      }));
    };

    const schedule = () => {
      if (scheduled.current !== null) return;
      scheduled.current = window.setTimeout(async () => {
        scheduled.current = null;
        if (inflight.current) await inflight.current;
        inflight.current = flush();
        await inflight.current;
        inflight.current = null;
      }, 30);
    };


    const enqueue = (nodes: Text[]) => {
      if (nodes.length === 0) return;
      // Instant apply from persistent cache — no waiting for edge function
      applyCachedNow(nodes);
      for (const n of nodes) pending.current.add(n);
      schedule();
    };

    // Initial pass
    const initial: Text[] = [];
    collectTextNodes(document.body, initial);
    enqueue(initial);

    // Observe for future changes
    const observer = new MutationObserver((mutations) => {
      const found: Text[] = [];
      for (const m of mutations) {
        if (m.type === "characterData") {
          const t = m.target as Text;
          const parent = t.parentElement;
          if (!parent || isSkippableAncestor(parent)) continue;
          const value = t.nodeValue ?? "";
          // If React overwrote our translation with original, re-translate
          if (isTranslatable(value)) found.push(t);
        } else if (m.type === "childList") {
          m.addedNodes.forEach((node) => {
            if (node.nodeType === Node.TEXT_NODE) {
              const t = node as Text;
              if (t.parentElement && !isSkippableAncestor(t.parentElement) && isTranslatable(t.nodeValue ?? "")) {
                found.push(t);
              }
            } else if (node.nodeType === Node.ELEMENT_NODE) {
              collectTextNodes(node, found);
            }
          });
        }
      }
      if (found.length > 0) enqueue(found);
    });

    observer.observe(document.body, {
      subtree: true,
      childList: true,
      characterData: true,
    });

    return () => {
      cancelled = true;
      observer.disconnect();
      if (scheduled.current !== null) {
        clearTimeout(scheduled.current);
        scheduled.current = null;
      }
    };
  }, [lang]);

  return null;
};

export default AutoTranslator;
